import React, { useLayoutEffect, useRef } from 'react';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import {
  Author,
  AuthorJobDescription,
  CurrentQuote,
  ExpandableQuoteContainer,
  OldQuote,
  Quote,
  QuoteContainer,
  testimonialAnimationTime,
} from './styles';

type QuoteContentProps = {
  quote: string;
  authorName?: string;
  authorJobDescription?: string;
} & ComponentProps;

export type TestimonialQuoteProps = {
  content: QuoteContentProps;
  oldContent?: QuoteContentProps;
} & ComponentProps;

export const QuoteContent: React.FC<QuoteContentProps> = ({
  quote,
  authorName,
  authorJobDescription,
  ...props
}) => {
  const authorSeparator = ' \u2014 ';

  return (
    <QuoteContainer tabIndex={0} {...componentProps(props)}>
      <Quote>{quote}</Quote>
      {(authorName || authorJobDescription) && (
        <Author data-testid="testimonial-author">
          {authorName && authorName}
          {authorName && authorJobDescription && authorSeparator}
          {authorJobDescription && (
            <AuthorJobDescription>{authorJobDescription}</AuthorJobDescription>
          )}
        </Author>
      )}
    </QuoteContainer>
  );
};

const TestimonialQuote: React.FC<TestimonialQuoteProps> = ({
  content,
  oldContent,
  ...props
}) => {
  const quoteContainerRef = useRef<HTMLDivElement>(null);
  const currentQuoteRef = useRef<HTMLDivElement>(null);
  const oldQuoteRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const quoteContainerElement = quoteContainerRef.current;
    const currentQuoteElement = currentQuoteRef.current;
    const oldQuoteElement = oldQuoteRef.current;

    if (!quoteContainerElement || !currentQuoteElement || !oldQuoteElement) {
      return;
    }

    const oldHeight = oldQuoteElement.clientHeight;
    const newHeight = currentQuoteElement.clientHeight;
    const bounce = 5 * Math.sign(newHeight - oldHeight);

    quoteContainerElement.animate(
      [
        { height: `${oldHeight}px` },
        { height: `${newHeight + bounce}px`, offset: 0.6 },
        { height: `${newHeight}px` },
      ],
      testimonialAnimationTime
    );
    oldQuoteElement.animate(
      [
        { opacity: 1, visibility: 'visible' },
        { opacity: 0, visibility: 'hidden' },
      ],
      testimonialAnimationTime / 2
    );
    currentQuoteElement.animate(
      [
        { opacity: 0, visibility: 'hidden' },
        { opacity: 1, visibility: 'visible' },
      ],
      testimonialAnimationTime
    );
  }, [oldContent, content]);

  return (
    <ExpandableQuoteContainer
      aria-live="polite"
      ref={quoteContainerRef}
      {...componentProps(props)}>
      <CurrentQuote key={content.quote + 'New'} ref={currentQuoteRef}>
        <QuoteContent {...content} />
      </CurrentQuote>
      {oldContent && (
        <OldQuote key={content.quote + 'Old'} aria-hidden={true} ref={oldQuoteRef}>
          <QuoteContent aria-hidden={true} {...oldContent} />
        </OldQuote>
      )}
    </ExpandableQuoteContainer>
  );
};

export default applyComponentTheme(TestimonialQuote, 'TestimonialQuote');
